import {
  IonButton,
  IonButtons,
  IonContent,
  IonFooter,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonListHeader,
  IonRefresher,
  IonRefresherContent,
  IonSearchbar,
  IonText,
  IonTitle,
  IonToolbar
} from "@ionic/react";
import {MenuButton} from "app/employee/menu/MenuButton";
import {
  addOutline,
  checkmarkCircle,
  chevronForward,
  closeOutline,
  createOutline,
  trashBinOutline
} from "ionicons/icons";
import {useTranslation} from "react-i18next";
import {useFuzzySearch} from "app/employee/controlCard/hook/useFuzzySearch";
import {Fragment, type ReactNode, useEffect, useMemo, useState} from "react";
import type {IonItemProps, MaybeNil} from "app/utils/types";
import {EMPTY_ARRAY, isNil, isNotBlank, isNotNil, normalizeWhitespace} from "app/utils/stdlib";
import {HomeBackButton} from "app/employee/menu/HomeBackButton";
import {useBoolean} from "@fluentui/react-hooks";
import type {AsyncOrSync} from "ts-essentials";
import {useLoadingContext} from "app/employee/LoadingContext";
import {styled} from "styled-components";
import type {EditComboBoxOptionOutcome} from "app/employee/controlCard/types";

export interface ComboBoxProps<Option> {
  title?: ReactNode;
  options?: MaybeNil<Option[]>;
  value?: MaybeNil<Option[]>;
  textExtractor: (option: Option) => string;
  // Opt-in: when provided, fuzzy matching runs against this extended haystack
  // (e.g. name + position + location) while rows keep rendering textExtractor
  // plainly. Trades <mark> highlighting for broader matching.
  searchTextExtractor?: (option: Option) => string;
  // Opt-in: when provided and non-blank, rendered as a muted second line under
  // the primary text. Purely visual; does not affect search.
  secondaryTextExtractor?: (option: Option) => MaybeNil<string>;
  searchPlaceholder?: string;
  keyExtractor: (option: Option) => string;
  onSelect?: (option: MaybeNil<Option[]>) => AsyncOrSync<void>;
  onCreate?: MaybeNil<(text: MaybeNil<string>) => AsyncOrSync<EditComboBoxOptionOutcome<Option>>>;
  onEdit?: MaybeNil<(value: Option) => AsyncOrSync<EditComboBoxOptionOutcome<Option>>>;
  onDelete?: MaybeNil<(value: Option) => AsyncOrSync<EditComboBoxOptionOutcome<Option>>>;
  refetch?: () => AsyncOrSync<void>;
  multiselect?: boolean;
  toggleMode?: boolean;
  groupExtractor?: (option: Option) => [number, string];
}

const CheckedLabel = styled(IonLabel)<{$checked: boolean}>`
  ${({$checked}) => $checked && `font-weight: bold;`}
`;

const GroupHeader = styled(IonLabel)`
  font-size: 1rem;
  font-weight: bold;
`;

export const ComboBox = <Option,>({
  title,
  options,
  value,
  textExtractor,
  searchTextExtractor,
  secondaryTextExtractor,
  searchPlaceholder,
  keyExtractor,
  onSelect,
  onCreate,
  onEdit,
  onDelete,
  refetch,
  multiselect,
  toggleMode,
  groupExtractor
}: ComboBoxProps<Option>) => {
  const {t} = useTranslation();

  const [selectedValues, setSelectedValues] = useState<Option[]>(() =>
    toggleMode && value ? [...value] : EMPTY_ARRAY
  );
  const [searchValue, setSearchValue] = useState("");
  const {startLoading, finishLoading, loading} = useLoadingContext();

  const normalizedSearchValue = normalizeWhitespace(searchValue);

  const toggleSelectedValue = (option: Option) => {
    setSelectedValues((prevSelectedValues) => {
      const index = prevSelectedValues.findIndex((v) => keyExtractor(v) === keyExtractor(option));
      if (index === -1) {
        return [...prevSelectedValues, option];
      } else {
        return prevSelectedValues.filter((_, i) => i !== index);
      }
    });
  };

  const addNewOption = async () => {
    const result = await onCreate?.(normalizedSearchValue);
    if (result?.success) {
      startLoading();
      try {
        await refetch?.();
        toggleSelectedValue(result.option);
        if (!multiselect) {
          await onSelect?.([result.option]);
        } else {
          setSearchValue("");
          cancelEditMode();
        }
      } finally {
        finishLoading();
      }
    }
  };

  const {filteredItems} = useFuzzySearch(
    normalizedSearchValue,
    options ?? EMPTY_ARRAY,
    searchTextExtractor ?? textExtractor
  );

  const groupedItems = useMemo(() => {
    if (!groupExtractor) return null;
    const groups = new Map<string, {order: number; items: typeof filteredItems}>();
    for (const item of filteredItems) {
      const [order, label] = groupExtractor(item.item);
      let group = groups.get(label);
      if (!group) {
        group = {order, items: []};
        groups.set(label, group);
      }
      group.items.push(item);
    }
    return [...groups.entries()].sort(([, a], [, b]) => a.order - b.order);
  }, [filteredItems, groupExtractor]);

  const [editMode, {toggle: toggleEditMode, setFalse: cancelEditMode}] = useBoolean(false);

  const canEdit = !loading;

  const toolbar = (!isNil(onCreate) || !isNil(onDelete) || !isNil(onEdit)) && filteredItems.length !== 0 && (
    <IonItem button={false} color="highlight" lines="full">
      {!isNil(onDelete) ||
        (!isNil(onEdit) && (
          <IonButton disabled={!canEdit} fill="outline" onClick={toggleEditMode} slot="start">
            <IonIcon icon={editMode ? closeOutline : !isNil(onDelete) ? trashBinOutline : createOutline} slot="start" />
            {editMode ? t("g.cancel") : !isNil(onDelete) ? t("g.delete") : t("g.edit")}
          </IonButton>
        ))}
      {!isNil(onCreate) && (
        <IonButton disabled={!canEdit} onClick={addNewOption} slot="end">
          <IonIcon icon={addOutline} slot="start" />
          {t("g.add")}
        </IonButton>
      )}
    </IonItem>
  );

  const addNewButton = !isNil(onCreate) && filteredItems.length === 0 && (
    <IonItem button={false} color="highlight" lines="full">
      <IonLabel>
        <IonButton disabled={!canEdit} expand="block" onClick={addNewOption} size="default">
          <IonIcon icon={addOutline} slot="start" />
          {t("g.add")}
        </IonButton>
      </IonLabel>
    </IonItem>
  );

  useEffect(() => {
    if (toggleMode) {
      setSelectedValues(value ? [...value] : EMPTY_ARRAY);
      return;
    }
    const optionsByKey = new Map<string, Option>();
    if (!isNil(options)) {
      for (const option of options) {
        optionsByKey.set(keyExtractor(option), option);
      }
    }
    setSelectedValues((prevValue) => {
      return prevValue
        .map((v) => {
          const key = keyExtractor(v);
          return optionsByKey.get(key);
        })
        .filter(isNotNil);
    });
  }, [keyExtractor, options, toggleMode, value]);

  return (
    <>
      <IonHeader>
        <IonToolbar>
          <HomeBackButton />
          <IonTitle>{title}</IonTitle>
          <MenuButton />
        </IonToolbar>
        <IonToolbar>
          <IonSearchbar
            debounce={200}
            disabled={loading}
            onIonInput={(value) => setSearchValue(value.detail.value ?? "")}
            placeholder={searchPlaceholder ?? t("g.combo-box.placeholder")}
            showClearButton="always"
            value={searchValue}
          />
        </IonToolbar>
      </IonHeader>

      <IonContent>
        {!isNil(refetch) && (
          <IonRefresher onIonRefresh={refetch} slot="fixed">
            <IonRefresherContent />
          </IonRefresher>
        )}
        <IonList>
          {toolbar}
          {addNewButton}
          {(groupedItems ?? [[null, {items: filteredItems}] as const]).map(([groupLabel, {items}]) => (
            <Fragment key={groupLabel ?? "__flat__"}>
              {groupLabel && (
                <IonListHeader color="highlight" lines="full">
                  <GroupHeader>{groupLabel}</GroupHeader>
                </IonListHeader>
              )}
              {items.map((filteredItem) => {
                const {item, highlighted} = filteredItem;

                let itemProps: IonItemProps;
                const itemKey = keyExtractor(item);

                const isChecked = toggleMode
                  ? selectedValues.some((v) => keyExtractor(v) === itemKey)
                  : value?.some((v) => keyExtractor(v) === itemKey);
                const checkmark =
                  isChecked && !editMode ? <IonIcon color="success" icon={checkmarkCircle} slot="end" /> : null;

                const handleEditItem = async () => {
                  const outcome = await onEdit?.(item);
                  if (outcome?.success) {
                    startLoading();
                    try {
                      await refetch?.();
                      setSearchValue("");
                      cancelEditMode();
                    } finally {
                      finishLoading();
                    }
                  }
                };

                let isSelected = false;
                if (multiselect) {
                  if (toggleMode) {
                    itemProps = {
                      button: !editMode,
                      onClick: editMode ? handleEditItem : () => toggleSelectedValue(item)
                    };
                  } else {
                    itemProps = {
                      button: editMode ? true : !isChecked,
                      onClick: editMode ? handleEditItem : isChecked ? undefined : () => toggleSelectedValue(item)
                    };
                    isSelected = editMode ? false : selectedValues.some((v) => keyExtractor(v) === itemKey);
                  }
                } else {
                  itemProps = {
                    button: true,
                    onClick: editMode ? handleEditItem : () => onSelect?.([item]),
                    detail: !(isChecked || editMode)
                  };
                }

                const secondary = secondaryTextExtractor?.(item);

                return (
                  <IonItem
                    color={isSelected ? "primary" : undefined}
                    detail={false}
                    key={itemKey}
                    {...itemProps}
                    disabled={loading}
                    lines="full"
                  >
                    {editMode && <IonIcon color="primary" icon={createOutline} slot="start" />}
                    <CheckedLabel $checked={isSelected} className="ion-text-wrap">
                      {isNil(searchTextExtractor) ? highlighted : textExtractor(item)}
                      {isNotBlank(secondary) && (
                        <IonText color={isSelected ? undefined : "medium"}> · {secondary}</IonText>
                      )}
                    </CheckedLabel>
                    {checkmark}
                  </IonItem>
                );
              })}
            </Fragment>
          ))}
        </IonList>
      </IonContent>
      {multiselect && (
        <IonFooter>
          <IonToolbar>
            <IonButtons slot="primary">
              <IonButton
                color="primary"
                disabled={selectedValues.length === 0 || loading || editMode}
                fill="outline"
                onClick={() => setSelectedValues([])}
              >
                <IonIcon icon={trashBinOutline} slot="start" />
                {t("g.clear")}
              </IonButton>

              <IonButton
                color="primary"
                disabled={loading || (!toggleMode && selectedValues.length === 0)}
                fill="solid"
                onClick={() => onSelect?.(selectedValues)}
              >
                <IonIcon icon={chevronForward} slot="end" />
                {t("g.continue", {count: selectedValues.length})}
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonFooter>
      )}
    </>
  );
};
